/**
 * The five Settings › Accounting entries this piece owns (piece 23): the
 * non-returnable-line gate, the finance charge's rate and escalation target,
 * the cash-basis cost-of-goods election, the cost basis and the default
 * reporting basis.
 *
 * 🔨 Two of them are effective-dated and the store holds one row per key, so
 * the dated series lives inside that row's value as a list of dated entries,
 * newest last. A reader asks for the entry in force on a date; nothing reads
 * the raw value.
 */

import { t } from '../strings';

/** Settings › Accounting › Non-Returnable Line on a Credit Note (piece 15's entry; piece 23 owns the rule). */
export const NON_RETURNABLE_LINE_KEY = 'creditNote.nonReturnableLine';
export const NON_RETURNABLE_LINE_VALUES = ['Warn', 'Refuse'] as const;
export type NonReturnableLineValue = (typeof NON_RETURNABLE_LINE_VALUES)[number];
/** 🔨 The setting is a gate at the moment of the act, so it carries no effective dates and defaults to Warn. */
export const NON_RETURNABLE_LINE_DEFAULT: NonReturnableLineValue = 'Warn';

/** Settings › Accounting › Cost basis: FIFO or WeightedAverage, effective-dated, shipping unset (decision 0062). */
export const COST_BASIS_KEY = 'inventory.costBasis';
export const COST_BASIS_VALUES = ['FIFO', 'WeightedAverage'] as const;
export type CostBasisValue = (typeof COST_BASIS_VALUES)[number];

/** What each basis reads as on the Settings screen; the member is what is stored (found in review, 2026-09-15). */
export const COST_BASIS_LABELS: Readonly<Record<CostBasisValue, string>> = {
  FIFO: t('accounting.costBasis.fifo'),
  WeightedAverage: t('accounting.costBasis.weightedAverage'),
};

/** Settings › Accounting › Finance charge: the rate, effective-dated, and the escalation target; both ship empty. */
export const FINANCE_CHARGE_RATE_KEY = 'ar.financeChargeRate';
export const FINANCE_CHARGE_ESCALATION_KEY = 'ar.financeChargeEscalationEmploymentId';
/** The job clock's hour key for runFinanceCharges, seeded at 7. */
export const FINANCE_CHARGE_RUN_HOUR_KEY = 'ar.financeChargeRunHour';
export const FINANCE_CHARGE_RUN_HOUR_DEFAULT = 7;

/** One dated entry of a setting's series: the value, and the day it takes effect. */
export interface DatedSetting<T = string | number> {
  effectiveFrom: string;
  value: T;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The stored value read back as a series: a bare value is read as one entry effective for ever, so an older row still answers. */
export function datedEntries<T = string | number>(stored: unknown): DatedSetting<T>[] {
  if (stored === null || stored === undefined || stored === '') return [];
  if (Array.isArray(stored)) {
    const rows = stored.filter((row): row is DatedSetting<T> => typeof row === 'object' && row !== null && DAY.test(String((row as DatedSetting<T>).effectiveFrom ?? '')));
    return [...rows].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  }
  return [{ effectiveFrom: '0001-01-01', value: stored as T }];
}

/** 🔨 The entry in force on a day is the latest one dated on or before it; a day before the first entry has no value at all. */
export function datedValueOn<T = string | number>(stored: unknown, on: Date | string): T | null {
  const day = typeof on === 'string' ? on.slice(0, 10) : on.toISOString().slice(0, 10);
  const rows = datedEntries<T>(stored).filter((row) => row.effectiveFrom <= day);
  return rows.length === 0 ? null : rows[rows.length - 1]!.value;
}

/** The series with one entry added or replaced: two entries never share a date, because the reader would have to choose. */
export function withDatedEntry<T = string | number>(stored: unknown, entry: DatedSetting<T>): DatedSetting<T>[] {
  const rows = datedEntries<T>(stored).filter((row) => row.effectiveFrom !== entry.effectiveFrom);
  return [...rows, entry].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
}

/** The series with the entry of one date removed, so a rate typed by mistake comes off again. */
export function withoutDatedEntry<T = string | number>(stored: unknown, effectiveFrom: string): DatedSetting<T>[] {
  return datedEntries<T>(stored).filter((row) => row.effectiveFrom !== effectiveFrom);
}

/** A date a dated entry can carry: a calendar day, and nothing else. */
export function effectiveFromProblem(effectiveFrom: string | null | undefined): string | null {
  if (!effectiveFrom || !DAY.test(effectiveFrom)) return 'an effective-dated setting names the calendar day it takes effect, as YYYY-MM-DD; pick the day this value starts applying';
  return null;
}

/** 🔨 A member of the closed list or nothing: an unset setting is an absent row, never a value nobody chose. */
export function memberProblem(value: string, allowed: readonly string[], what: string): string | null {
  if (!allowed.includes(value)) return `${what} is ${allowed.join(' or ')}; ${value || 'nothing'} is not one of them`;
  return null;
}

export function costBasisProblem(value: string, effectiveFrom: string | null | undefined): string | null {
  return memberProblem(value, COST_BASIS_VALUES, 'the cost basis') ?? effectiveFromProblem(effectiveFrom);
}

/**
 * 🔨 The rate is a percentage per period, and the system enforces no cap: the
 * lawful figure is the operator's to take advice on. A rate at or below zero
 * is refused, because a charge nobody is charging is an absent row.
 */
export function financeChargeRateProblem(rate: unknown, effectiveFrom: string | null | undefined): string | null {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return 'a finance-charge rate is a percentage per period';
  if (rate <= 0) return 'a finance-charge rate above zero is what makes a charge; clear the row instead of setting zero';
  return effectiveFromProblem(effectiveFrom);
}

/** 🔒 The page the cost basis is set on, which the stock-leaving refusal carries so the screen can draw a link (T1191). */
export const COST_BASIS_ROUTE = '/settings/accounting/basis';

/** The sentence the stock-leaving path refuses with while the cost basis is unset (settled while building, 2026-09-14). */
export function costBasisUnsetMessage(): string {
  return `no cost basis is set: choose FIFO or WeightedAverage in Settings › Accounting › Accounting Basis before stock leaves, or a cost of goods posts at a figure nobody chose`;
}

/** The sentence the finance-charge job reports while no rate is in force. */
export function financeChargeRateUnsetMessage(): string {
  return `no finance-charge rate is in force: a super admin sets one in Settings › Accounting › Finance Charge (${FINANCE_CHARGE_RATE_KEY})`;
}

/** What a Credit Note may do with a line whose returnable value is false, once the setting in force is read. */
export type NonReturnableOutcome = { kind: 'allowed' } | { kind: 'bypass'; message: string } | { kind: 'refused'; message: string };

/**
 * 🔨 The gate at the moment of the act (piece 23): `Warn` reuses the existing
 * bypass — a reason and an authoriser against the note — and `Refuse` blocks
 * the line with no override, the returnable value being edited on the source
 * document instead. 💰 Both leave the same evidence, so a setting change never
 * changes what is recoverable.
 */
export function nonReturnableLineOutcome(setting: NonReturnableLineValue, line: { returnable: boolean }, bypass: { reason?: string | null; authoriserPersonId?: string | null } | null): NonReturnableOutcome {
  if (line.returnable) return { kind: 'allowed' };
  if (setting === 'Refuse') return { kind: 'refused', message: 'this line is not returnable and the setting refuses it; edit the line on the source document instead' };
  if (bypass?.reason?.trim() && bypass.authoriserPersonId) return { kind: 'allowed' };
  return { kind: 'bypass', message: 'this line is not returnable: record a reason and an authorizer against the note to credit it anyway' };
}
